/**
 * VinylRecord — the core visual component.
 * A matte black vinyl disc with groove rings, a center label,
 * and optional slow rotation when playing.
 */

interface VinylRecordProps {
  playing?: boolean;
  size?: number;           // px diameter (default 320)
  labelContent?: React.ReactNode;
  onPlayToggle?: () => void;
  className?: string;
  grooveGlow?: boolean;
}

export default function VinylRecord({
  playing = false,
  size = 320,
  labelContent,
  onPlayToggle,
  className = "",
  grooveGlow = false,
}: VinylRecordProps) {
  const r = size / 2;

  // Groove ring radii as fractions of outer radius
  const grooveRatios = [0.97, 0.93, 0.89, 0.85, 0.81, 0.77, 0.73, 0.69, 0.65, 0.61, 0.57, 0.53];

  return (
    <div
      className={`relative select-none ${playing ? "vinyl-spinning" : ""} ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Base vinyl sheen gradient */}
          <radialGradient id={`vinyl-base-${size}`} cx="42%" cy="38%" r="60%">
            <stop offset="0%"   stopColor="hsl(0 0% 12%)" />
            <stop offset="40%"  stopColor="hsl(0 0% 7%)" />
            <stop offset="100%" stopColor="hsl(0 0% 4%)" />
          </radialGradient>

          {/* Groove glow (when playing) */}
          <radialGradient id={`groove-glow-${size}`} cx="50%" cy="50%" r="50%">
            <stop offset="50%"  stopColor="hsl(354 68% 40% / 0)" />
            <stop offset="80%"  stopColor={`hsl(354 50% 30% / ${grooveGlow ? 0.025 : 0})`} />
            <stop offset="100%" stopColor={`hsl(354 50% 30% / ${grooveGlow ? 0.05 : 0})`} />
          </radialGradient>

          {/* Label gradient */}
          <radialGradient id={`label-grad-${size}`} cx="50%" cy="44%" r="55%">
            <stop offset="0%"   stopColor="hsl(0 0% 14%)" />
            <stop offset="100%" stopColor="hsl(0 0% 9%)" />
          </radialGradient>

          <clipPath id={`disc-clip-${size}`}>
            <circle cx={r} cy={r} r={r} />
          </clipPath>
        </defs>

        {/* Outer disc */}
        <circle cx={r} cy={r} r={r} fill={`url(#vinyl-base-${size})`} />

        {/* Groove glow overlay */}
        <circle cx={r} cy={r} r={r} fill={`url(#groove-glow-${size})`}
          className={grooveGlow && playing ? "groove-pulse" : ""} />

        {/* Groove rings */}
        {grooveRatios.map((ratio, i) => (
          <circle
            key={i}
            cx={r}
            cy={r}
            r={r * ratio}
            fill="none"
            stroke={`hsl(0 0% ${grooveGlow && playing ? 20 : 13}%)`}
            strokeWidth="0.6"
            opacity={0.7 - i * 0.035}
          />
        ))}

        {/* Subtle sheen line */}
        <ellipse
          cx={r * 0.72}
          cy={r * 0.55}
          rx={r * 0.18}
          ry={r * 0.04}
          fill="hsl(0 0% 100% / 0.025)"
          transform={`rotate(-30, ${r}, ${r})`}
        />

        {/* Center label area */}
        <circle cx={r} cy={r} r={r * 0.30} fill={`url(#label-grad-${size})`} />
        <circle cx={r} cy={r} r={r * 0.30} fill="none" stroke="hsl(0 0% 18%)" strokeWidth="0.8" />

        {/* Spindle hole */}
        <circle cx={r} cy={r} r={r * 0.024} fill="hsl(0 0% 3%)" />
      </svg>

      {/* Label content overlay (centered in label area) */}
      {labelContent && (
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
          style={{ width: size * 0.58, height: size * 0.58 }}
        >
          {labelContent}
        </div>
      )}

      {/* Click overlay for play/pause */}
      {onPlayToggle && (
        <button
          onClick={onPlayToggle}
          className="absolute inset-0 rounded-full focus:outline-none"
          aria-label={playing ? "Pause" : "Play"}
        />
      )}
    </div>
  );
}
